import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { Page } from '../models/cadastro.model';
import { Lead, EvolucaoDiaria, Usage, OrcamentoMesApi, VendaMesApi, OrcamentoVendasMesApi, LeadsMesApi, IndicadoresFinanceiros, ProdutoMaisVendidoApi, OrcamentosPorDiaSemanaApi, VendasPorDiaSemanaApi, VendaPedrasPorMesApi, DespesaGrupoApi, DespesaSubgrupoApi, TopClienteApi } from '../models/analytics.model';
import { AgrupamentoContasAPagar, MovimentoFinanceiroMensalItem, RelatorioContasAPagarAgrupado, RelatorioContasAPagarDetalhado, RelatorioOrcamento, RelatorioProdutoVendidoItem, RelatorioVendasDetalhado, TipoDataContasAPagar } from '../models/financeiro.model';
import { ConfigService } from '../core/config/config.service';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {

    private readonly configService = inject(ConfigService);
  

    private get loginUrl(): string {
      const baseUrl = this.configService.settings()?.apiUrl;
      return `${baseUrl}`;
    }


  constructor(private http: HttpClient) {
    
  }

  saveLead(lead: Lead, id: number): Observable<Lead> {
    
    return this.http.post<Lead>(this.loginUrl +'/analytics/leads/' + id, lead);
  }

  deleteLead(id: number): Observable<void> {
    return this.http.delete<void>(this.loginUrl + '/analytics/leads/' + id);
  }

  getLeadById(id: number): Observable<Lead> {
    return this.http.get<Lead>(this.loginUrl + '/analytics/leads/' + id);
  }

  saveUsage(usage: Usage): Observable<Usage> {
    return this.http.post<Usage>(this.loginUrl +'/analytics/usage-logs', usage);
  }

  contador(data: string): Observable<number> {
      return this.http.get<number>(this.loginUrl +'/analytics/leads/contador/' + data);
  }

   evolucaoDiaria(): Observable<EvolucaoDiaria> {
    return this.http.get<EvolucaoDiaria>(this.loginUrl +'/analytics/leads/evolucaoDiaria');
  }

  leadsPorIntervaloDatas(dataInicial: string, dataFinal: string, page: number, size: number): Observable<Page<Lead>> {
      let params = new HttpParams()
      .set('dataInicial', dataInicial)
      .set('dataFinal', dataFinal)
      .set('page', page.toString())
      .set('size', size.toString());

      return this.http.get<Page<Lead>>(this.loginUrl +'/analytics/leads/data/intervalo', {params});
  }


   classificarLeads(): Observable<void> {
      return this.http.get<void>(this.loginUrl +'/analytics/leads/classificarLeads');
   }


   leadsPorData(data: string): Observable<Lead[]> {
      return this.http.get<Lead[]>(this.loginUrl +'/analytics/leads/data/' + data);
   }

   getOrcamentosPorMes(): Observable<OrcamentoMesApi[]> {
    return this.http.get<OrcamentoMesApi[]>(this.loginUrl +'/analytics/dashboard/orcamentos-por-mes');
  }

  getLeadsPorMes(): Observable<LeadsMesApi[]> {
    return this.http.get<LeadsMesApi[]>(this.loginUrl + '/analytics/dashboard/leads-por-mes');
  }

  getVendasPorMes(): Observable<VendaMesApi[]> {
    return this.http.get<VendaMesApi[]>(this.loginUrl + '/analytics/dashboard/vendas-por-mes');
  }


  getVendasOrcamentosPorMes(dataInicial: string, dataFinal: string): Observable<OrcamentoVendasMesApi[]> {
    const params = new HttpParams()
      .set('dataInicial', dataInicial)
      .set('dataFinal', dataFinal);
    return this.http.get<OrcamentoVendasMesApi[]>(
      this.loginUrl + '/analytics/dashboard/vendas-orcamentos-por-mes', { params }
    );
  }

  getLeadsIndicadorMes(): Observable<number> {
    return this.http
      .get(this.loginUrl + '/analytics/dashboard/leads-por-mes', { responseType: 'text' })
      .pipe(map((value) => this.parseIndicadorNumerico(value)));
  }

  getOrcamentosIndicadorMes(): Observable<number> {
    return this.http
      .get(this.loginUrl + '/analytics/dashboard/orcamentos-por-mes', { responseType: 'text' })
      .pipe(map((value) => this.parseIndicadorNumerico(value)));
  }

  getVendasIndicadorMes(): Observable<number> {
    return this.http
      .get(this.loginUrl + '/analytics/dashboard/vendas-por-mes', { responseType: 'text' })
      .pipe(map((value) => this.parseIndicadorNumerico(value)));
  }

  getLeadsPorPeriodo(dataInicial: string, dataFinal: string): Observable<number> {
    const params = new HttpParams().set('dataInicial', dataInicial).set('dataFinal', dataFinal);
    return this.http
      .get(this.loginUrl + '/analytics/dashboard/leads-por-mes', { responseType: 'text', params })
      .pipe(map((v) => this.parseIndicadorNumerico(v)));
  }

  getOrcamentosPorPeriodo(dataInicial: string, dataFinal: string): Observable<number> {
    const params = new HttpParams().set('dataInicial', dataInicial).set('dataFinal', dataFinal);
    return this.http
      .get(this.loginUrl + '/analytics/dashboard/orcamentos-por-mes', { responseType: 'text', params })
      .pipe(map((v) => this.parseIndicadorNumerico(v)));
  }

  getVendasPorPeriodo(dataInicial: string, dataFinal: string): Observable<number> {
    const params = new HttpParams().set('dataInicial', dataInicial).set('dataFinal', dataFinal);
    return this.http
      .get(this.loginUrl + '/analytics/dashboard/vendas-por-mes', { responseType: 'text', params })
      .pipe(map((v) => this.parseIndicadorNumerico(v)));
  }

  getIndicadoresFinanceiros(dataInicial: string, dataFinal: string): Observable<IndicadoresFinanceiros> {
    const params = new HttpParams()
      .set('dataInicial', dataInicial)
      .set('dataFinal', dataFinal);

    return this.http.get<IndicadoresFinanceiros>(this.loginUrl + '/analytics/dashboard/indicadoresFinanceiros', { params });
  }

  getTopClientes(): Observable<TopClienteApi[]> {
    return this.http.get<any>(this.loginUrl + '/analytics/dashboard/top-clientes').pipe(
      map(r => Array.isArray(r) ? r : (r?.content ?? []))
    );
  }

  getProdutosMaisVendidos(dataInicial: string, dataFinal: string): Observable<ProdutoMaisVendidoApi[]> {
    const params = new HttpParams()
      .set('dataInicial', dataInicial)
      .set('dataFinal', dataFinal);
    return this.http.get<ProdutoMaisVendidoApi[]>(this.loginUrl + '/analytics/dashboard/produtos-mais-vendidos', { params });
  }

  getMediaOrcamentosPorDiaSemana(): Observable<OrcamentosPorDiaSemanaApi[]> {
    return this.http.get<OrcamentosPorDiaSemanaApi[]>(this.loginUrl + '/analytics/dashboard/media-orcamentos-por-dia-semana');
  }

  getMediaVendasPorDiaSemana(): Observable<VendasPorDiaSemanaApi[]> {
    return this.http.get<VendasPorDiaSemanaApi[]>(this.loginUrl + '/analytics/dashboard/media-vendas-por-dia-semana');
  }

  getVendaPedrasPorMes(dataInicial: string, dataFinal: string): Observable<VendaPedrasPorMesApi[]> {
    const params = new HttpParams()
      .set('dataInicial', dataInicial)
      .set('dataFinal', dataFinal);
    return this.http.get<VendaPedrasPorMesApi[]>(this.loginUrl + '/analytics/dashboard/venda-pedras-por-mes', { params });
  }

  getDespesasPorGrupo(dataInicial: string, dataFinal: string): Observable<DespesaGrupoApi[]> {
    const params = new HttpParams()
      .set('dataInicial', dataInicial)
      .set('dataFinal', dataFinal);
    return this.http.get<DespesaGrupoApi[]>(
      this.loginUrl + '/analytics/dashboard/despesas-por-grupo', { params }
    );
  }

  getDespesasPorSubgrupo(dataInicial: string, dataFinal: string): Observable<DespesaSubgrupoApi[]> {
    const params = new HttpParams()
      .set('dataInicial', dataInicial)
      .set('dataFinal', dataFinal);
    return this.http.get<DespesaSubgrupoApi[]>(
      this.loginUrl + '/analytics/dashboard/despesas-por-subgrupo', { params }
    );
  }

  getRelatorioOrcamentos(statusOrcamento?: string, dataInicial?: string, dataFinal?: string): Observable<RelatorioOrcamento[]> {
    let params = new HttpParams();
    if (statusOrcamento) {
      params = params.set('statusOrcamento', statusOrcamento);
    }
    if (dataInicial) {
      params = params.set('dataInicial', dataInicial);
    }
    if (dataFinal) {
      params = params.set('dataFinal', dataFinal);
    }
    return this.http.get<RelatorioOrcamento[]>(this.loginUrl + '/analytics/relatorios/orcamentos', { params });
  }

  getRelatorioMovimentoFinanceiroMensal(
    anoInicial: string,
    mesInicial: string,
    anoFinal: string,
    mesFinal: string
  ): Observable<MovimentoFinanceiroMensalItem[]> {
    const params = new HttpParams()
      .set('anoInicial', anoInicial)
      .set('mesInicial', mesInicial)
      .set('anoFinal', anoFinal)
      .set('mesFinal', mesFinal);
    return this.http.get<MovimentoFinanceiroMensalItem[]>(
      this.loginUrl + '/analytics/relatorios/movimento-financeiro/resumo', { params }
    );
  }

  getRelatorioVendasDetalhado(
    dataVendaInicial?: string,
    dataVendaFinal?: string,
    idVendedor?: number,
    statusVenda?: string,
    formaPagamento?: string
  ): Observable<RelatorioVendasDetalhado> {
    let params = new HttpParams();
    if (dataVendaInicial) params = params.set('dataVendaInicial', dataVendaInicial);
    if (dataVendaFinal) params = params.set('dataVendaFinal', dataVendaFinal);
    if (idVendedor) params = params.set('idVendedor', idVendedor.toString());
    if (statusVenda) params = params.set('statusVenda', statusVenda);
    if (formaPagamento) params = params.set('formaPagamento', formaPagamento);
    return this.http.get<RelatorioVendasDetalhado>(
      this.loginUrl + '/analytics/relatorios/vendas/detalhado', { params }
    );
  }

  getRelatorioContasAPagarDetalhado(
    dataVencimentoInicial?: string,
    dataVencimentoFinal?: string,
    dataPagamentoInicial?: string,
    dataPagamentoFinal?: string,
    grupoId?: number,
    subgrupoId?: number,
    fornecedorId?: number
  ): Observable<RelatorioContasAPagarDetalhado> {
    let params = new HttpParams();
    if (dataVencimentoInicial) params = params.set('dataVencimentoInicial', dataVencimentoInicial);
    if (dataVencimentoFinal) params = params.set('dataVencimentoFinal', dataVencimentoFinal);
    if (dataPagamentoInicial) params = params.set('dataPagamentoInicial', dataPagamentoInicial);
    if (dataPagamentoFinal) params = params.set('dataPagamentoFinal', dataPagamentoFinal);
    if (grupoId) params = params.set('grupoId', grupoId.toString());
    if (subgrupoId) params = params.set('subgrupoId', subgrupoId.toString());
    if (fornecedorId) params = params.set('fornecedorId', fornecedorId.toString());
    return this.http.get<RelatorioContasAPagarDetalhado>(
      this.loginUrl + '/analytics/relatorios/contas-a-pagar/detalhado', { params }
    );
  }

  getRelatorioContasAPagarAgrupado(
    tipoData: TipoDataContasAPagar,
    dataInicial: string,
    dataFinal: string,
    agrupamento: AgrupamentoContasAPagar,
    status?: string
  ): Observable<RelatorioContasAPagarAgrupado> {
    let params = new HttpParams()
      .set('tipoData', tipoData)
      .set('dataInicial', dataInicial)
      .set('dataFinal', dataFinal)
      .set('agrupamento', agrupamento);

    // Sem status significa "todos"; o backend so filtra quando o parametro vem.
    if (status) {
      params = params.set('status', status);
    }

    return this.http.get<RelatorioContasAPagarAgrupado>(
      this.loginUrl + '/analytics/relatorios/contas-a-pagar/agrupado', { params }
    );
  }

  getRelatorioProdutosVendidos(dataInicial: string, dataFinal: string): Observable<RelatorioProdutoVendidoItem[]> {
    const params = new HttpParams()
      .set('dataInicial', dataInicial)
      .set('dataFinal', dataFinal);
    return this.http.get<any>(this.loginUrl + '/analytics/relatorios/produtos-vendidos', { params }).pipe(
      map(r => Array.isArray(r) ? r : (r?.content ?? []))
    );
  }

  private parseIndicadorNumerico(value: string): number {
    if (!value) {
      return 0;
    }

    const normalized = value
      .replace(/\./g, '')
      .replace(',', '.')
      .replace(/[^0-9.-]/g, '')
      .trim();

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }
}

